import { useState, useMemo, useEffect, useCallback } from 'react';
import { useWeeklyPlan, type WeeklyPlanItem, type NewPlanItem } from '@/hooks/useWeeklyPlan';
import { AddPlanItemModal } from './AddPlanItemModal';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { ChevronLeft, ChevronRight, Plus, Dumbbell, Utensils, Moon, Coffee, Check, Trash2, Pencil, CalendarDays } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

const getWeekDates = (offset: number) => {
  const now = new Date();
  const monday = new Date(now);
  monday.setDate(now.getDate() - ((now.getDay() + 6) % 7) + offset * 7);
  const dates: string[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    dates.push(d.toISOString().split('T')[0]);
  }
  return dates;
};

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const TYPE_CONFIG: Record<string, { icon: any; color: string; dot: string }> = {
  workout: { icon: Dumbbell, color: 'bg-blue-500/20 text-blue-400', dot: 'bg-blue-400' },
  meal: { icon: Utensils, color: 'bg-green-500/20 text-green-400', dot: 'bg-green-400' },
  sleep: { icon: Moon, color: 'bg-purple-500/20 text-purple-400', dot: 'bg-purple-400' },
  rest_day: { icon: Coffee, color: 'bg-amber-500/20 text-amber-400', dot: 'bg-amber-400' },
};

const PlanItemRow = ({ item, onToggle, onEdit, onDelete }: {
  item: WeeklyPlanItem;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) => {
  const config = TYPE_CONFIG[item.item_type] || TYPE_CONFIG.workout;
  const Icon = config.icon;
  return (
    <div className={`flex items-center gap-2 p-2 rounded-xl ${config.color} ${item.is_completed ? 'opacity-50' : ''}`}>
      <Icon className="w-4 h-4 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium truncate ${item.is_completed ? 'line-through' : ''}`}>{item.title}</p>
        {item.scheduled_time && <p className="text-[10px] opacity-70">{item.scheduled_time.slice(0, 5)}</p>}
        {item.item_type === 'sleep' && item.sleep_time && item.wake_time && (
          <p className="text-[10px] opacity-70">{item.sleep_time.slice(0, 5)} → {item.wake_time.slice(0, 5)}</p>
        )}
        {item.item_type === 'meal' && item.meal_slot && (
          <p className="text-[10px] opacity-70 capitalize">{item.meal_slot}</p>
        )}
      </div>
      <div className="flex items-center gap-1">
        <button onClick={onToggle} className={`p-1 rounded-md transition-colors ${item.is_completed ? 'bg-green-500/30' : 'hover:bg-foreground/10'}`}>
          <Check className="w-3.5 h-3.5" />
        </button>
        <button onClick={onEdit} className="p-1 rounded-md hover:bg-foreground/10 transition-colors">
          <Pencil className="w-3.5 h-3.5" />
        </button>
        <button onClick={onDelete} className="p-1 rounded-md hover:bg-destructive/20 text-destructive/70 transition-colors">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export const WeeklyCalendarWidget = () => {
  const [weekOffset, setWeekOffset] = useState(0);
  const weekDates = useMemo(() => getWeekDates(weekOffset), [weekOffset]);
  const today = new Date().toISOString().split('T')[0];
  const [selectedDate, setSelectedDate] = useState(today);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState<WeeklyPlanItem | null>(null);
  const [overviewOpen, setOverviewOpen] = useState(false);
  const [overviewMonthOffset, setOverviewMonthOffset] = useState(0);
  const [overviewItems, setOverviewItems] = useState<WeeklyPlanItem[]>([]);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const { user } = useAuth();

  const { items, loading, addItem, updateItem, deleteItem, toggleComplete } = useWeeklyPlan(
    weekDates[0],
    weekDates[6]
  );

  const itemsByDate = useMemo(() => {
    const map: Record<string, WeeklyPlanItem[]> = {};
    items.forEach(item => {
      if (!map[item.plan_date]) map[item.plan_date] = [];
      map[item.plan_date].push(item);
    });
    return map;
  }, [items]);

  const selectedItems = itemsByDate[selectedDate] || [];

  const effectiveSelected = weekDates.includes(selectedDate) ? selectedDate : weekDates[0];
  if (effectiveSelected !== selectedDate) {
    setTimeout(() => setSelectedDate(effectiveSelected), 0);
  }

  const weekLabel = useMemo(() => {
    const start = new Date(weekDates[0] + 'T12:00:00');
    const end = new Date(weekDates[6] + 'T12:00:00');
    const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
    return `${start.toLocaleDateString('en-US', opts)} – ${end.toLocaleDateString('en-US', opts)}`;
  }, [weekDates]);

  // Month overview data
  const overviewMonth = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + overviewMonthOffset, 1);
  }, [overviewMonthOffset]);

  const overviewMonthLabel = useMemo(() => {
    return overviewMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }, [overviewMonth]);

  const overviewDates = useMemo(() => {
    const year = overviewMonth.getFullYear();
    const month = overviewMonth.getMonth();
    const lastDay = new Date(year, month + 1, 0).getDate();
    const dates: string[] = [];
    for (let d = 1; d <= lastDay; d++) {
      const mm = String(month + 1).padStart(2, '0');
      const dd = String(d).padStart(2, '0');
      dates.push(`${year}-${mm}-${dd}`);
    }
    return dates;
  }, [overviewMonth]);

  const fetchOverviewItems = useCallback(async () => {
    if (!user || !overviewOpen) return;
    setOverviewLoading(true);
    try {
      const { data, error } = await supabase
        .from('weekly_plan_items')
        .select('*')
        .eq('user_id', user.id)
        .gte('plan_date', overviewDates[0])
        .lte('plan_date', overviewDates[overviewDates.length - 1])
        .order('plan_date')
        .order('sort_order');
      if (!error) setOverviewItems((data as WeeklyPlanItem[]) || []);
    } catch (e) {
      console.error('Error fetching overview items:', e);
    } finally {
      setOverviewLoading(false);
    }
  }, [user, overviewOpen, overviewDates]);

  useEffect(() => {
    if (overviewOpen) fetchOverviewItems();
  }, [overviewOpen, fetchOverviewItems]);

  const overviewItemsByDate = useMemo(() => {
    const map: Record<string, WeeklyPlanItem[]> = {};
    overviewItems.forEach(item => {
      if (!map[item.plan_date]) map[item.plan_date] = [];
      map[item.plan_date].push(item);
    });
    return map;
  }, [overviewItems]);

  const overviewDaysToShow = overviewDates;

  return (
    <div className="w-full max-w-md">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold text-foreground">Weekly Plan</h3>
          <Sheet open={overviewOpen} onOpenChange={(open) => { setOverviewOpen(open); if (open) setOverviewMonthOffset(0); }}>
            <SheetTrigger asChild>
              <button className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-primary">
                <CalendarDays className="w-4 h-4" />
              </button>
            </SheetTrigger>
            <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-2xl">
              <SheetHeader className="pb-1">
                <div className="flex items-center justify-between">
                  <SheetTitle className="text-foreground text-base">Month Overview</SheetTitle>
                </div>
                <div className="flex items-center justify-center gap-3 pt-1">
                  <button onClick={() => setOverviewMonthOffset(o => o - 1)} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
                    <ChevronLeft className="w-4 h-4 text-muted-foreground" />
                  </button>
                  <span className="text-sm font-medium text-foreground min-w-[140px] text-center">{overviewMonthLabel}</span>
                  <button onClick={() => setOverviewMonthOffset(o => o + 1)} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </button>
                </div>
              </SheetHeader>
              {overviewLoading ? (
                <p className="text-xs text-muted-foreground text-center py-8">Loading...</p>
              ) : overviewDaysToShow.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-8 italic">No plans this month</p>
              ) : (
                <div className="space-y-3 pb-6 pt-2">
                  {overviewDaysToShow.map(date => {
                    const dayItems = overviewItemsByDate[date] || [];
                    const isToday = date === today;
                    const dateObj = new Date(date + 'T12:00:00');
                    const dayLabel = dateObj.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                    return (
                      <div key={date} className={`rounded-xl border p-3 ${isToday ? 'border-primary/40 bg-primary/5' : 'border-border/50 bg-card'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className={`text-sm font-semibold ${isToday ? 'text-primary' : 'text-foreground'}`}>
                            {dayLabel}
                            {isToday && <span className="ml-2 text-[10px] bg-primary/20 text-primary px-1.5 py-0.5 rounded-full">Today</span>}
                          </span>
                          <button
                            onClick={() => { setSelectedDate(date); setEditingItem(null); setShowAddModal(true); setOverviewOpen(false); }}
                            className="p-1 rounded-md hover:bg-muted transition-colors text-muted-foreground"
                          >
                            <Plus className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        {dayItems.length === 0 ? (
                          <p className="text-xs text-muted-foreground italic">No plans</p>
                        ) : (
                          <div className="space-y-1.5">
                            {dayItems.map(item => (
                              <PlanItemRow
                                key={item.id}
                                item={item}
                                onToggle={() => { toggleComplete(item.id, !item.is_completed); fetchOverviewItems(); }}
                                onEdit={() => { setEditingItem(item); setSelectedDate(date); setShowAddModal(true); setOverviewOpen(false); }}
                                onDelete={() => { deleteItem(item.id); fetchOverviewItems(); }}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </SheetContent>
          </Sheet>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setWeekOffset(w => w - 1)} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
            <ChevronLeft className="w-4 h-4 text-muted-foreground" />
          </button>
          <span className="text-xs text-muted-foreground min-w-[120px] text-center">{weekLabel}</span>
          <button onClick={() => setWeekOffset(w => w + 1)} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
      </div>

      {/* Day strip */}
      <div className="flex gap-1 mb-3">
        {weekDates.map((date, i) => {
          const dayNum = new Date(date + 'T12:00:00').getDate();
          const isSelected = date === selectedDate;
          const isToday = date === today;
          const dayItems = itemsByDate[date] || [];
          const uniqueTypes = [...new Set(dayItems.map(it => it.item_type))];

          return (
            <button
              key={date}
              onClick={() => setSelectedDate(date)}
              className={`flex-1 flex flex-col items-center py-2 rounded-2xl transition-all ${
                isSelected
                  ? ''
                  : isToday
                  ? 'bg-muted/60 border border-border/50'
                  : 'border border-transparent hover:bg-muted/40'
              }`}
              style={isSelected ? {
                backgroundColor: 'rgba(0,255,163,0.12)',
                border: '1px solid rgba(0,255,163,0.40)',
                boxShadow: '0 0 15px rgba(0,255,163,0.3), inset 0 1px 1px rgba(0,255,163,0.15)',
              } : undefined}
            >
              <span className={`text-[10px] font-medium ${isSelected ? 'text-primary' : 'text-muted-foreground'}`}>
                {DAY_LABELS[i]}
              </span>
              <span className={`text-sm font-bold ${isSelected ? 'text-primary' : isToday ? 'text-foreground' : 'text-foreground/70'}`}>
                {dayNum}
              </span>
              <div className="flex gap-0.5 mt-1 h-1.5">
                {uniqueTypes.slice(0, 3).map(type => (
                  <div key={type} className={`w-1.5 h-1.5 rounded-full ${TYPE_CONFIG[type]?.dot || 'bg-muted-foreground'}`} />
                ))}
              </div>
            </button>
          );
        })}
      </div>

      {/* Selected day detail */}
      <div
        className="rounded-3xl bg-white/[0.03] backdrop-blur-xl p-3 min-h-[100px]"
        style={{
          borderWidth: '1px',
          borderStyle: 'solid',
          borderTopColor: 'rgba(255,255,255,0.20)',
          borderLeftColor: 'rgba(255,255,255,0.15)',
          borderRightColor: 'rgba(255,255,255,0.05)',
          borderBottomColor: 'rgba(255,255,255,0.05)',
          boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.3)',
        }}
      >
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-foreground">
            {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
          </span>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0 text-primary hover:bg-primary/10"
            onClick={() => { setEditingItem(null); setShowAddModal(true); }}
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>

        {loading ? (
          <p className="text-xs text-muted-foreground text-center py-4">Loading...</p>
        ) : selectedItems.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-xs text-muted-foreground mb-2">No plans for this day</p>
            <Button
              size="sm"
              variant="outline"
              className="text-xs border-border/50"
              onClick={() => { setEditingItem(null); setShowAddModal(true); }}
            >
              <Plus className="w-3 h-3 mr-1" /> Add Plan
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {selectedItems.map(item => (
              <PlanItemRow
                key={item.id}
                item={item}
                onToggle={() => toggleComplete(item.id, !item.is_completed)}
                onEdit={() => { setEditingItem(item); setShowAddModal(true); }}
                onDelete={() => deleteItem(item.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Tip */}
      <p className="text-[10px] text-muted-foreground text-center mt-2">
        💡 Ask your AI coach to plan your week
      </p>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <AddPlanItemModal
          open={showAddModal}
          onClose={() => { setShowAddModal(false); setEditingItem(null); }}
          onAdd={addItem}
          date={selectedDate}
          editItem={editingItem ? {
            id: editingItem.id,
            item_type: editingItem.item_type,
            title: editingItem.title,
            description: editingItem.description,
            meal_slot: editingItem.meal_slot,
            scheduled_time: editingItem.scheduled_time,
            sleep_time: editingItem.sleep_time,
            wake_time: editingItem.wake_time,
          } : undefined}
          onUpdate={updateItem}
        />
      )}
    </div>
  );
};
